/**
 * GenerateControls Component
 * Clean AI-powered composition generation interface for desktop
 */

import { useState, useCallback, useEffect } from 'react';
import { generateAPI } from '../../services/api/GenerateAPI';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCredits, InsufficientCreditsError } from '../../hooks/useCredits';
import { activityTrackingService } from '../../services/user/ActivityTrackingService';
import { trackEvent } from '../../services/metaPixel/metaPixelLoader';
import { CreditPurchaseModal } from '../auth/CreditPurchaseModal';
import type { Composition } from '../../types/composition';
import './GenerateControls.css';

export interface GenerateControlsProps {
  isAuthenticated: boolean;
  currentComposition?: Composition;
  onCompositionGenerated: (composition: Composition) => void;
  onInsufficientCredits?: (required: number, available: number) => void;
}

export function GenerateControls({
  isAuthenticated,
  currentComposition,
  onCompositionGenerated,
  onInsufficientCredits,
}: GenerateControlsProps) {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusColor, setStatusColor] = useState('');
  const [loadingDots, setLoadingDots] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [progressBar, setProgressBar] = useState(0);
  const [showEditNotice, setShowEditNotice] = useState(false);
  const { signIn } = useAuthContext();
  const { credits, consumeCredit, hasCredits } = useCredits();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const loadingMessages = [
    '🎮 LOADING SOUND CHIP',
    '🎵 COMPOSING MELODY',
    '🎹 ARRANGING CHANNELS',
    '🎸 TUNING INSTRUMENTS',
    '🥁 PROGRAMMING DRUMS',
    '🎼 MIXING TRACKS',
    '✨ ADDING 8-BIT MAGIC',
    '🔊 ADJUSTING VOLUMES',
    '🎶 CRAFTING HARMONIES',
    '⚡ POWERING UP SYNTHS',
    '🌟 FINALIZING COMPOSITION',
    '🎺 CALIBRATING FREQUENCIES',
    '🎻 LAYERING MELODIES',
    '🔮 GENERATING PATTERNS',
    '💫 SYNCING RHYTHMS',
    '🎪 ORCHESTRATING SOUNDS',
    '🌈 PAINTING AUDIO WAVES',
    '🚀 LAUNCHING SEQUENCES',
    '⭐ POLISHING NOTES',
    '🎭 BALANCING DYNAMICS',
    '🔥 IGNITING CREATIVITY',
    '💎 REFINING TONES',
    '🌙 MOONWALKING BASSLINES',
    '☄️ DROPPING THE BEAT',
    '🎯 TARGETING PERFECTION',
  ];

  const hasNotes = useCallback((): boolean => {
    if (!currentComposition) return false;
    return currentComposition.channels.some((ch) => ch.notes.length > 0);
  }, [currentComposition]);

  const updateStatus = useCallback((message: string, color: string = '') => {
    setStatusMessage(message);
    setStatusColor(color);
  }, []);

  useEffect(() => {
    if (isGenerating) {
      const interval = setInterval(() => {
        setLoadingDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setLoadingDots('');
    }
    return undefined;
  }, [isGenerating]);

  useEffect(() => {
    if (isGenerating) {
      const messageInterval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      let progress = 0;
      const progressInterval = setInterval(() => {
        const remaining = 90 - progress;
        const increment = Math.max(0.5, remaining * 0.08);
        progress = Math.min(progress + increment, 90);
        setProgressBar(progress);
      }, 1000);
      return () => {
        clearInterval(messageInterval);
        clearInterval(progressInterval);
      };
    } else {
      setLoadingMessageIndex(0);
      setProgressBar(0);
    }
    return undefined;
  }, [isGenerating, loadingMessages.length]);

  useEffect(() => {
    if (statusMessage && !isGenerating) {
      const timer = setTimeout(() => {
        setStatusMessage('');
        setStatusColor('');
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [statusMessage, isGenerating]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      updateStatus('⚠️ Please enter a prompt first.', 'crimson');
      return;
    }
    if (!isAuthenticated) {
      updateStatus('⚠️ Please sign in to use AI generation.', 'crimson');
      return;
    }
    const requiredCredits = 1;
    if (!hasCredits(requiredCredits)) {
      updateStatus('❌ Insufficient credits. Please purchase more credits.', 'crimson');
      onInsufficientCredits?.(requiredCredits, credits);
      return;
    }

    setIsGenerating(true);
    setLoadingMessageIndex(0);
    setProgressBar(0);
    const isEditing = hasNotes();

    trackEvent('AIGenerate', { content_name: 'AI Composition', content_category: isEditing ? 'edit' : 'new', mode: isEditing ? 'edit' : 'new' });

    try {
      const result = await generateAPI.generate(
        prompt.trim(),
        isEditing ? currentComposition : undefined
      );
      try {
        const remainingCredits = await consumeCredit('generate', requiredCredits);
        setProgressBar(100);
        onCompositionGenerated(result);
        try {
          await activityTrackingService.trackAIGeneration({
            prompt: prompt.trim(),
            mode: isEditing ? 'edit' : 'new',
            timestamp: Date.now(),
          });
        } catch (trackingError) {
          console.warn('[GenerateControls] Activity tracking failed:', trackingError);
        }
        updateStatus(`✅ LEVEL COMPLETE! (${remainingCredits} credits remaining)`, 'green');
        setPrompt('');
      } catch (creditError) {
        if (creditError instanceof InsufficientCreditsError) {
          updateStatus('❌ Insufficient credits. Please purchase more credits.', 'crimson');
          onInsufficientCredits?.(requiredCredits, credits);
        } else {
          console.error('[GenerateControls] Failed to consume credit:', creditError);
          updateStatus('❌ Failed to deduct credit. Please try again.', 'crimson');
        }
      }
    } catch (error) {
      console.error('[GenerateControls] Generation failed', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      updateStatus(`❌ Generation failed: ${errorMessage}`, 'crimson');
    } finally {
      setIsGenerating(false);
    }
  }, [
    prompt, isAuthenticated, credits, currentComposition,
    hasNotes, hasCredits, consumeCredit,
    onCompositionGenerated, onInsufficientCredits, updateStatus,
  ]);

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newPrompt = e.target.value;
      setPrompt(newPrompt);
      
      // Show edit notice if they have existing composition and start typing
      if (newPrompt.trim() && hasNotes()) {
        setShowEditNotice(true);
      } else {
        setShowEditNotice(false);
      }
    },
    [hasNotes]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleGenerate();
      }
    },
    [handleGenerate]
  );

  if (!isAuthenticated) {
    return (
      <section id="generate-section" className="generate-section" aria-label="AI music generation">
        <div className="generate-signin">
          <div className="generate-signin-text">
            🔒{' '}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); signIn(); }}
              className="generate-signin-link"
              aria-label="Sign in to use AI generation"
            >
              Sign in
            </a>
            {' '}to use AI generation
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="generate-section" className="generate-section" aria-label="AI music generation">
      <h2 className="sr-only">AI-Powered Music Generation</h2>
      <div id="generate-controls" className="generate-controls">

        {/* Loading State */}
        {isGenerating ? (
          <div className="generate-loading">
            <div className="generate-loading-message">
              <span className="generate-loading-icon">🎮</span>
              <span className="generate-loading-text">
                {loadingMessages[loadingMessageIndex]}{loadingDots}
              </span>
            </div>
            <div className="generate-progress-container">
              <div className="generate-progress-bar" style={{ width: `${progressBar}%` }}>
                <div className="generate-progress-scanline" />
              </div>
              <div className="generate-progress-text">{Math.round(progressBar)}%</div>
            </div>
            <div className="generate-loading-tip">⚡ PLEASE WAIT - DO NOT POWER OFF ⚡</div>
          </div>
        ) : (
          <>
            {/* Edit Mode Notice */}
            {showEditNotice && (
              <div className="generate-edit-notice">
                <i className="fa-solid fa-info-circle generate-edit-notice-icon"></i>
                <span className="generate-edit-notice-text">
                  AI will edit your current composition. Click "Fresh Slate" to start from scratch.
                </span>
              </div>
            )}

            {/* Status */}
            {statusMessage && (
              <div className="generate-status" style={{ color: statusColor || 'var(--color-fg)' }} aria-live="polite">
                <span>{statusMessage}</span>
              </div>
            )}

            {/* Input Row: textarea + button side by side */}
            <div className="generate-input-row">
              <textarea
                id="generate-prompt"
                className="generate-prompt"
                rows={2}
                value={prompt}
                onChange={handlePromptChange}
                onKeyDown={handleKeyDown}
                disabled={isGenerating}
                placeholder="Describe the type of 8-bit track you want (e.g., 'upbeat battle theme' or 'peaceful village music')..."
                aria-label="Music generation prompt"
              />
              <button
                id="generate-btn"
                className="generate-btn"
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
                aria-label="Generate music from prompt"
              >
                <i className="fa-solid fa-wand-magic-sparkles"></i>
                Generate
              </button>
            </div>

            {/* Pro Tips Row */}
            <div className="generate-tips-row">
              <div className="generate-tip-card">
                <div className="generate-tip-icon">🎵</div>
                <div className="generate-tip-text">
                  <strong>Be specific</strong> about mood & tempo
                </div>
              </div>
              <div className="generate-tip-card">
                <div className="generate-tip-icon">🎹</div>
                <div className="generate-tip-text">
                  <strong>Mention instruments</strong> like "square lead" or "triangle bass"
                </div>
              </div>
              <div className="generate-tip-card">
                <div className="generate-tip-icon">⚡</div>
                <div className="generate-tip-text">
                  <strong>Set length & tempo</strong> like "16 steps at 140 BPM"
                </div>
              </div>
              <div className="generate-tip-card">
                <div className="generate-tip-icon">🎼</div>
                <div className="generate-tip-text">
                  <strong>Describe the feel</strong> like "energetic" or "mysterious"
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <CreditPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
    </section>
  );
}
